// src/components/dashboard/RecentTransaction.js
import React, { useMemo } from 'react'
import { ListItem, ListItemText, ListItemIcon, Chip, Typography } from '@mui/material'
import { ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { useLanguage } from '../../contexts/LanguageContext'

const safeNumber = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
}

const pickStatus = (tx) => {
    return (
        tx?.status ||
        tx?.state ||
        tx?.transaction_status ||
        tx?.payment_status ||
        'Completed'
    )
}

const statusToKey = (statusRaw) => {
    const s = String(statusRaw || '').toLowerCase()
    if (s.includes('pend')) return 'pending'
    if (s.includes('fail') || s.includes('reject') || s.includes('cancel')) return 'failed'
    if (s.includes('complete') || s.includes('success') || s.includes('paid')) return 'completed'
    return 'completed'
}

const statusColor = (key) => {
    if (key === 'pending') return 'warning'
    if (key === 'failed') return 'error'
    return 'success'
}

const formatMoney = (amount, currency = 'USD') => {
    const n = safeNumber(amount)
    const c = String(currency || 'USD').toUpperCase()

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: c,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n)
    } catch (e) {
        const fixed = n.toFixed(2)
        return c === 'USD' ? `$${fixed}` : `${fixed} ${c}`
    }
}

const guessKind = (tx) => {
    const dir = String(tx?.direction || tx?.type || '').toLowerCase()
    const desc = String(tx?.description || tx?.title || tx?.note || '').toLowerCase()

    if (desc.includes('withdra') || dir.includes('withdraw')) return 'withdraw'
    if (desc.includes('depos') || dir.includes('deposit')) return 'deposit'

    if (dir.includes('sent') || dir.includes('debit')) return 'sent'
    if (dir.includes('received') || dir.includes('credit')) return 'received'

    const amount =
        tx?.amount ??
        tx?.total ??
        tx?.net_amount ??
        tx?.value ??
        0

    return safeNumber(amount) < 0 ? 'sent' : 'received'
}

const localStatusLabel = (statusKey, lang) => {
    if (lang !== 'fr') {
        if (statusKey === 'pending') return 'Pending'
        if (statusKey === 'failed') return 'Failed'
        return 'Completed'
    }
    if (statusKey === 'pending') return 'En attente'
    if (statusKey === 'failed') return 'Echec'
    return 'Termine'
}

const localizeTransactionText = (value, lang) => {
    const text = String(value || '').trim()
    if (lang !== 'fr' || !text) return text

    const normalized = text.toLowerCase()
    const labels = {
        received: 'Recu',
        sent: 'Envoye',
        completed: 'Termine',
        pending: 'En attente',
        failed: 'Echec',
        withdraw: 'Retrait',
        withdrawal: 'Retrait',
        deposit: 'Depot',
        funding: 'Recharge',
        'pay invoice': 'Paiement de facture',
        'invoice payment': 'Paiement de facture',
        'payment completed': 'Paiement termine',
        'wallet funded': 'Wallet recharge',
        transaction: 'Transaction',
    }

    return labels[normalized] || text
}

const translateKind = (kind, lang) => {
    if (kind === 'withdraw') return lang === 'fr' ? 'Retrait' : 'Withdraw'
    if (kind === 'deposit') return lang === 'fr' ? 'Depot' : 'Deposit'
    if (kind === 'sent') return lang === 'fr' ? 'Envoye' : 'Sent'
    if (kind === 'received') return lang === 'fr' ? 'Recu' : 'Received'
    return 'Transaction'
}

const buildPrimary = (tx, kind, lang) => {
    const desc = String(tx?.description || '').trim()
    if (desc) return localizeTransactionText(desc, lang)
    return localizeTransactionText(
        translateKind(kind, lang) || tx?.title || tx?.note || 'Transaction',
        lang
    )
}

const buildSecondary = (tx, statusLabel) => {
    const date =
        tx?.created_at ||
        tx?.date ||
        tx?.createdAt ||
        ''

    if (!date) return statusLabel
    return `${statusLabel} - ${String(date).slice(0, 10)}`
}

const RecentTransaction = ({ transaction }) => {
    const { lang } = useLanguage()

    const statusRaw = pickStatus(transaction)
    const statusKey = statusToKey(statusRaw)

    const statusLabel =
        localStatusLabel(statusKey, lang)

    const currency = transaction?.currency || transaction?.wallet_currency || 'USD'

    const rawAmount =
        transaction?.amount ??
        transaction?.total ??
        transaction?.net_amount ??
        transaction?.value ??
        0

    const kind = guessKind(transaction)

    const isNegative =
        kind === 'withdraw' ||
        kind === 'sent' ||
        safeNumber(rawAmount) < 0

    const absAmount = Math.abs(safeNumber(rawAmount))

    const primaryText = useMemo(
        () => buildPrimary(transaction, kind, lang),
        [kind, lang, transaction]
    )
    const secondaryText = useMemo(
        () => buildSecondary(transaction, statusLabel),
        [statusLabel, transaction]
    )

    return (
        <ListItem disableGutters sx={{ py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
                {isNegative ? (
                    <ArrowUpward sx={{ fontSize: 18, color: 'error.main' }} />
                ) : (
                    <ArrowDownward sx={{ fontSize: 18, color: 'success.main' }} />
                )}
            </ListItemIcon>

            <ListItemText
                primary={primaryText}
                secondary={secondaryText}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
            />

            <Typography
                variant="body2"
                sx={{ mr: 1, fontWeight: 700 }}
                color={isNegative ? 'error.main' : 'success.main'}
            >
                {isNegative ? '-' : '+'}
                {formatMoney(absAmount, currency)}
            </Typography>

            <Chip label={statusLabel} size="small" color={statusColor(statusKey)} />
        </ListItem>
    )
}

export default RecentTransaction
